import { ChallengeStatus } from '@/model';
import { z } from 'zod';
import { t } from '@/i18n';

const requiredText = t('utils.schema.required').toString();
const requiredImage = t('utils.schema.select_image').toString();

export const modalFormSchema = z
  .object({
    step: z.number().default(1),
    status: z
      .enum([ChallengeStatus.DRAFT, ChallengeStatus.NOT_PUBLISHED, ChallengeStatus.PUBLISHED])
      .default(ChallengeStatus.NOT_PUBLISHED),
    title: z.string().optional(),
    description: z.string().optional(),
    team_uuid: z.string().optional(),
    time_limit_seconds: z
      .number()
      .refine(value => {
        const num = Number(value);
        return isNaN(num) ? 0 : num;
      })
      .optional(),
    has_start_date: z.boolean().default(false),
    starts_in: z.string().optional(),

    timeout_message: z.string().optional(),
    error_message: z.string().optional(),
    winner_message: z.string().optional(),
    communication_message: z.string().optional(),

    questions: z
      .array(
        z.object({
          uuid: z.string().uuid(),
          is_editing: z.boolean().default(false),
          title: z.string().optional(),
          image: z.any().optional(),
          answer: z
            .array(
              z.object({
                uuid: z.string().uuid(),
                content: z.string().optional(),
                is_correct: z.boolean().default(false),
              }),
            )
            .default([]),
        }),
      )
      .default([]),
  })
  .superRefine((values, ctx) => {
    function validateStep1Fields() {
      if (!values.title) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['title'],
          message: requiredText,
        });
      }

      if (!values.description) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['description'],
          message: requiredText,
        });
      }

      if (!values.time_limit_seconds) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['time_limit_seconds'],
          message: requiredText,
        });
      }

      if (values.has_start_date && !values.starts_in) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['starts_in'],
          message: requiredText,
        });
      }
    }

    function validateStep2Fields() {
      if ((values?.questions ?? [])?.length < 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['questions'],
          message: t('pages.challenges_details.schema.at_least_one_question').toString(),
        });
      } else {
        values?.questions?.forEach((question, index) => {
          if (!question.title) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['questions', index, 'title'],
              message: requiredText,
            });
          }

          if (!question.image) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['questions', index, 'image'],
              message: requiredImage,
            });
          }

          if ((question?.answer?.length ?? 0) < 2) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['questions', index, 'answer'],
              message: t('pages.challenges_details.schema.at_least_two_alternatives').toString(),
            });
          }

          values?.questions?.[index]?.answer?.forEach((answer, answerIndex) => {
            if (!answer.content) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['questions', index, 'answer', answerIndex, 'content'],
                message: requiredText,
              });
            }
          });

          if (!question?.answer?.some(answer => answer.is_correct)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['questions', index, 'answer'],
              message: t('pages.challenges_details.schema.correct_alternative').toString(),
            });
          }
        });
      }
    }

    function validateStep3Fields() {
      if (!values.timeout_message) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['timeout_message'],
          message: requiredText,
        });
      }

      if (!values.error_message) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['error_message'],
          message: requiredText,
        });
      }

      if (!values.winner_message) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['winner_message'],
          message: requiredText,
        });
      }

      if (!values.communication_message) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['communication_message'],
          message: requiredText,
        });
      }
    }

    function validateAllSteps() {
      validateStep1Fields();
      validateStep2Fields();
      validateStep3Fields();
    }

    switch (values.step) {
      case 1:
        validateStep1Fields();
        break;
      case 2:
        validateStep2Fields();
        break;
      case 3:
        validateStep3Fields();
        break;
      default:
        validateAllSteps();
        break;
    }
  });

export type ModalFormData = z.infer<typeof modalFormSchema>;
